import { useMemo } from 'react'
import { buildingImagesRootGuid, documentationRootGuid } from '@constants'
import { Stack, Typography, Box, Alert } from '@mui/material'
import { LoadingIndicator } from '@ui/feedback'
import { Form2 } from '@ui/forms'
import { useProjectFiles } from '@editorHooks'
import BaseFormFields from '../../../BaseFormFields'

type DefaultValues = FileItemConfig | FolderItemConfig

interface Props {
  item: FileItem | SectionItem
  mutate: (data: FileItemConfig | FolderItemConfig) => void
}

const ProjectDataForm = ({ item, mutate }: Props) => {
  const defaultValues = useMemo(() => {
    return {
      ...item.additional_data,
      item_guid: item.guid,
      name: item.name,
    }
  }, [item])

  const { data: buildingImages, isFetching: isFetchingBuildingImages } =
    useProjectFiles(buildingImagesRootGuid)

  // Get the latest building image
  const latestImage = useMemo(() => {
    if (!buildingImages?.files?.length) return null

    return buildingImages.files
      .filter(file => file.mimetype?.startsWith('image/'))
      .sort((a, b) => new Date(b.created || '').getTime() - new Date(a.created || '').getTime())[0]
  }, [buildingImages])

  return (
    <Stack spacing={2} p={1}>
      <Form2
        key={JSON.stringify(defaultValues)}
        onSubmit={(data: DefaultValues) => {
          mutate(data)
        }}
        defaultValues={defaultValues}
        submitOnChange
      >
        <BaseFormFields
          elementGuid={'element_guid' in item ? item.element_guid : undefined}
          showBeginOnNewPage={item.parent_guid !== documentationRootGuid}
        />

        {/* Building Image Display */}
        <Stack sx={{ mt: 2 }} spacing={2}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Aktuelles Gebäudebild
          </Typography>
          {isFetchingBuildingImages ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
              }}
            >
              <LoadingIndicator />
            </Box>
          ) : latestImage ? (
            <Box
              sx={{
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 1,
                overflow: 'hidden',
                maxWidth: 300,
              }}
            >
              <Box
                component="img"
                src={latestImage.url || ''}
                alt={latestImage.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
              <Box sx={{ p: 1, bgcolor: 'grey.50', borderTop: 1, borderColor: 'grey.200' }}>
                <Typography variant="caption" color="text.secondary">
                  {latestImage.created
                    ? new Date(latestImage.created).toLocaleString()
                    : 'Unbekannt'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                bgcolor: 'grey.50',
                maxWidth: 300,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Kein Gebäudebild vorhanden
              </Typography>
            </Box>
          )}

          <Alert severity="info">
            Das Gebäudebild kann über das Screenshot-Tool im Editor hochgeladen werden
          </Alert>
        </Stack>
      </Form2>
    </Stack>
  )
}

export default ProjectDataForm
